import { UserType } from "@/api/query/useCurrentUserQuery";
import { ExtendPlanTypeEnumSchema, PlanTypeEnumSchema } from "@backend/src/schemas/enums";

/**
 * Checks if the Timesheet feature is accessible for the given user.
 * Accessible if:
 * 1. User has an ENTERPRISE subscription.
 * 2. User is within the 14-day free trial period.
 */
export const isTimesheetAccessible = (user: UserType | null | undefined): boolean => {
  if (!user) return false;

  // 1. Check for Enterprise plan
  if (user.subscription?.planType === PlanTypeEnumSchema.ENTERPRISE) {
    return true;
  }

  // 2. Check for 14-day free trial via customerId
  if (user.subscription?.customerId === ExtendPlanTypeEnumSchema.FREE_TRIAL) {
    return true;
  }

  // 3. Check for 14-day free trial via provider creation date
  // We use the first provider's FreeTrialCreatedAt as the start of the trial
  const trialStartDateStr = user.provider?.[0]?.FreeTrialCreatedAt;
  if (trialStartDateStr) {
    const trialStartDate = new Date(trialStartDateStr);
    const now = new Date();
    
    // Calculate difference in days
    const diffTime = Math.abs(now.getTime() - trialStartDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    const trialDays = user.provider?.[0]?.freeTrialDays || 14;
    if (diffDays <= trialDays) {
      return true;
    }
  }

  return false;
};

/**
 * Checks if the Project Budget feature is accessible for the given user.
 * Accessible only to customers with:
 * 1. BUSINESS or ENTERPRISE subscription.
 * 2. Active 14-day free trial period.
 */
export const isProjectBudgetAccessible = (user: UserType | null | undefined): boolean => {
  if (!user) return false;

  // 1. Check for Business or Enterprise plan
  const planType = user.subscription?.planType;
  if (
    planType === PlanTypeEnumSchema.BUSINESS ||
    planType === PlanTypeEnumSchema.ENTERPRISE
  ) {
    return true;
  }

  // 2. Check for 14-day free trial via customerId
  if (user.subscription?.customerId === ExtendPlanTypeEnumSchema.FREE_TRIAL) {
    return true;
  }

  // 3. Check for 14-day free trial via provider creation date
  const trialStartDateStr = user.provider?.[0]?.FreeTrialCreatedAt;
  if (trialStartDateStr) {
    const trialStartDate = new Date(trialStartDateStr);
    const now = new Date();

    const diffTime = Math.abs(now.getTime() - trialStartDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    const trialDays = user.provider?.[0]?.freeTrialDays || 14;
    if (diffDays <= trialDays) {
      return true;
    }
  }

  return false;
};
